import type { GridProps } from '@chakra-ui/react'
import { FormControl, FormLabel, Select, Spinner } from '@chakra-ui/react'
import type { AddonProductEnum, OfferAddon } from 'BrokerTsLib/src/dtos'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect, useState } from 'react'

import { Loading } from '@/components/Loading'
import { usePrintContext } from '@/context/PrintContext'

import { useInsuranceParams } from '../../../../../../context/InsuranceParamsProvider'
import { useOffersAddonContext } from '../../../../../../context/OffersAddonProvider'
import { useGetAddonProductEnums, useGetAddonRiskVariantProducts, useSelectVariant } from '../../../../../../hooks/useOfferAddon'
import { FormRow } from '../../../../../forms/FormRow'
import { FormContent } from '../../../../vehicle/Step2/forms/ExtendedInsuranceForm/components/FormContent'
import { getParamsDataArr } from './ExtendedInsuranceForm'

export const offerStatusEnum = {
  Valid: 'valid',
  Selected: 'selected',
  Error: 'error',
} as const

export type offerStatusEnum = typeof offerStatusEnum[keyof typeof offerStatusEnum]

type ExtendedInsurerOffersProps = {
  activeOffer: OfferAddon
  gridProps?: GridProps
  onIsLoading?: (isLoading: boolean) => void
  onChangedParams?: (anyChanged: boolean) => void
}

export const ExtendedInsurerOffers = ({
  activeOffer,
  gridProps
}: ExtendedInsurerOffersProps) => {
  const { printMode } = usePrintContext()
  const { isDisabled } = useInsuranceParams()
  const { refetchOffers: refetchAddonOffers } = useOffersAddonContext()
  const [addonProductEnums, setAddonProductEnums] = useState<AddonProductEnum[]>([])

  const { data: addonRiskVariantProducts, mutateAsync: getAddonRiskVariantProducts, isLoading: isLoadingAddonRiskVariantProducts } = useGetAddonRiskVariantProducts()
  const { mutateAsync: getAddonProductEnums, isLoading: isLoadingAddonProductEnums } = useGetAddonProductEnums(setAddonProductEnums)

  const { mutateAsync: selectVariant } = useSelectVariant()

  useEffect(() => {
    getAddonRiskVariantProducts({
      offerId: activeOffer?.offerId,
      riskVariantId: activeOffer?.riskVariantId,
    })

    getAddonProductEnums({
      offerId: activeOffer?.offerId,
      riskVariantId: activeOffer?.riskVariantId,
      changedParams: [],
      filterOutput: true
    })
  }, [])

  const { setValue, getValues, watch } = useFormContext()

  const changedParams =
    getParamsDataArr(getValues()).filter(
      (x) =>
        (activeOffer.params?.parameterValues.findIndex(
          (y) => y.name == x.name && y.value == x.value,
        ) ?? -1) < 0,
    ) ?? []

  const handleChangeVariant = (productId: string) => {
    selectVariant({ activeOfferId: activeOffer.offerId, productId: parseInt(productId) })
      .then((data) =>
        getAddonProductEnums({
          offerId: data.data,
          riskVariantId: activeOffer?.riskVariantId,
          changedParams: [],
          filterOutput: true
        }).then(() => refetchAddonOffers({}))
      )
  }

  const handleChangeParams = (paramName: string, paramValue: string) => {
    setValue(paramName, paramValue)
    const changedParamsModified = [...changedParams] // changedParams nemusí ještě obsahovat aktuálně měněný parametr, proto je nutné ho přidat
    const index = changedParamsModified.findIndex((x) => x.name == paramName)
    if (index >= 0) changedParamsModified[index].value = paramValue
    else changedParamsModified.push({ name: paramName, value: paramValue })
    getAddonProductEnums({
      offerId: activeOffer?.offerId,
      riskVariantId: activeOffer?.riskVariantId,
      changedParams: changedParamsModified,
      filterOutput: true
    })
  }

  const isLoading = isLoadingAddonRiskVariantProducts || isLoadingAddonProductEnums
  const anyVariants = addonRiskVariantProducts?.data && addonRiskVariantProducts.data.length > 0
  const anyParams = addonProductEnums.length > 0

  if (!anyVariants && !anyParams && !isLoading)
    return null

  if (isLoading)
    return <Loading large={true} text='' />


  return (
    <FormContent>
      <FormRow itemsCount={2} {...gridProps} sx={{ rowGap: 4, ...gridProps?.sx }}>
        {anyVariants && (
          <FormControl>
            <FormLabel>Varianta</FormLabel>

            <Select
              id="variant"
              variant='outline'
              value={activeOffer.productId.toString()}
              disabled={addonRiskVariantProducts?.data.length == 1 || isDisabled || printMode}
              onChange={(e) => {
                handleChangeVariant(e.target.value)
              }}
            >
              {addonRiskVariantProducts?.data.map((x) => (
                <option key={x.id} value={x.id.toString()}>
                  {x.name}
                </option>
              ))}
            </Select>
          </FormControl>
        )}

        {addonProductEnums.map((x) => (
          <FormControl key={x.paramName}>
            <FormLabel>{x.paramDescription}</FormLabel>

            <Select
              id={x.paramName}
              variant='outline'
              value={watch(x.paramName)?.toString()}
              disabled={x.values.length == 1 || isDisabled || printMode}
              onChange={(e) => handleChangeParams(x.paramName, e.target.value)}
              icon={isLoadingAddonProductEnums ? <Spinner /> : undefined}
            >
              {x.values?.map((product) => (
                <option key={product.value} value={product.value.toString()}>
                  {product.text}
                </option>
              ))}
            </Select>
          </FormControl>
        ))}
      </FormRow>
    </FormContent>
  )
}
